// SOW Data Serialization Utilities
// Functions for serializing SOWData to JSON and deserializing back.
// Includes validation to ensure loaded data is structurally correct.

import { PropertyType } from './sow.types';
import type {
  SOWData,
  CategoryData,
  FieldValues,
  ContingencyMode,
  CurrencyValue,
} from './sow.types';
import { sowConfig, isValidCategoryId, isValidFieldId } from './sow.config';
import { initializeSOWData } from './data';
import { recalculateTotals, calculateContingency } from './calculations';

// ============================================
// TYPES
// ============================================

// Serialized format - same as SOWData but with explicit version
export interface SerializedSOWData extends SOWData {
  /** Schema version for future migrations */
  _version: number;
  /** Timestamp when serialized */
  _timestamp: string;
}

// Validation result
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// ============================================
// CONSTANTS
// ============================================

/** Current schema version */
export const SCHEMA_VERSION = 1;

// ============================================
// VALIDATION UTILITIES
// ============================================

/** Result of a single validation check */
interface ValidatorResult {
  valid: boolean;
  error?: string;
}

/** A validator function that checks a value and returns a result */
type Validator<T = unknown> = (value: T, fieldName?: string) => ValidatorResult;

// --- Base Validators ---

/** Validates that a value is a non-null object */
const isObject: Validator = (value, fieldName = 'Value') => {
  if (!value || typeof value !== 'object') {
    return { valid: false, error: `${fieldName} must be an object` };
  }
  return { valid: true };
};

/** Validates that a value is a non-negative number */
const isNonNegativeNumber: Validator = (value, fieldName = 'Value') => {
  if (typeof value !== 'number' || isNaN(value) || value < 0) {
    return { valid: false, error: `${fieldName} must be a non-negative number` };
  }
  return { valid: true };
};

/** Validates that a value is a positive integer */
const isPositiveInteger: Validator = (value, fieldName = 'Value') => {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1) {
    return { valid: false, error: `${fieldName} must be a positive integer` };
  }
  return { valid: true };
};

/** Validates that an optional value, if present, is a string */
const isOptionalString: Validator = (value, fieldName = 'Value') => {
  if (value !== undefined && typeof value !== 'string') {
    return { valid: false, error: `${fieldName} must be a string` };
  }
  return { valid: true };
};

/** Validates that a value is within a numeric range (inclusive) */
const isInRange = (min: number, max: number): Validator<number> => (value, fieldName = 'Value') => {
  if (value < min || value > max) {
    return { valid: false, error: `${fieldName} must be between ${min} and ${max}` };
  }
  return { valid: true };
};

/** Validates that a value is one of the allowed values */
const isOneOf = <T>(allowedValues: readonly T[]): Validator<T> => (value, fieldName = 'Value') => {
  if (!allowedValues.includes(value)) {
    return { valid: false, error: `${fieldName} must be one of: ${allowedValues.join(', ')}` };
  }
  return { valid: true };
};

// --- Validator Composition ---

/** Combines multiple validators - all must pass for the value to be valid */
function composeValidators<T>(...validators: Validator<T>[]): Validator<T> {
  return (value, fieldName) => {
    for (const validator of validators) {
      const result = validator(value, fieldName);
      if (!result.valid) {
        return result;
      }
    }
    return { valid: true };
  };
}

/** Collects errors from a validation check, pushing to the errors array if invalid */
function collectError(result: ValidatorResult, errors: string[]): boolean {
  if (!result.valid && result.error) {
    errors.push(result.error);
    return false;
  }
  return true;
}

// ============================================
// SERIALIZATION
// ============================================

// Serialize SOWData to a JSON string
export function serializeSOWData(data: SOWData): string {
  const serialized: SerializedSOWData = {
    ...data,
    _version: SCHEMA_VERSION,
    _timestamp: new Date().toISOString(),
  };
  return JSON.stringify(serialized, null, 2);
}

// Serialize SOWData to a plain object (for API calls)
export function serializeToObject(data: SOWData): SerializedSOWData {
  return {
    ...data,
    _version: SCHEMA_VERSION,
    _timestamp: new Date().toISOString(),
  };
}

// ============================================
// DESERIALIZATION
// ============================================

// Deserialize JSON string to SOWData
// Returns null if parsing fails
export function deserializeSOWData(json: string): SOWData | null {
  try {
    const parsed = JSON.parse(json);
    return deserializeFromObject(parsed);
  } catch {
    return null;
  }
}

// Deserialize from a plain object
// Validates and normalizes the data
export function deserializeFromObject(obj: unknown): SOWData | null {
  const validation = validateSOWData(obj);

  if (!validation.isValid) {
    return null;
  }

  // Normalize and return
  return normalizeSOWData(obj as Partial<SOWData>);
}

// ============================================
// VALIDATION
// ============================================

// Validate that an object conforms to SOWData structure
export function validateSOWData(obj: unknown): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Use isObject validator for the initial object check
  const objectCheck = isObject(obj, 'Data');
  if (!objectCheck.valid) {
    return { isValid: false, errors: [objectCheck.error!], warnings: [] };
  }

  const data = obj as Record<string, unknown>;

  // Check version - warn on mismatch (data may still load but could have compatibility issues)
  if ('_version' in data && data._version !== SCHEMA_VERSION) {
    warnings.push(`Schema version mismatch: expected ${SCHEMA_VERSION}, got ${data._version}`);
  }

  // Validate propertyType using isOneOf validator
  const propertyTypeValidator = isOneOf([PropertyType.SFR, PropertyType.Condo, PropertyType.Townhouse, PropertyType.MultiUnit] as const);
  const propertyTypeResult = propertyTypeValidator(data.propertyType as PropertyType.SFR | PropertyType.Condo | PropertyType.Townhouse, 'propertyType');
  const propertyType = propertyTypeResult.valid ? (data.propertyType as PropertyType.SFR | PropertyType.Condo | PropertyType.Townhouse) : null;
  if (!propertyType) {
    errors.push(`Invalid propertyType: ${data.propertyType}`);
  }

  // Validate unitCount using composed validators
  const unitCountValidator = composeValidators<number>(
    isPositiveInteger as Validator<number>,
    isInRange(1, sowConfig.maxUnits)
  );
  const unitCountResult = unitCountValidator(data.unitCount as number, 'unitCount');
  if (!unitCountResult.valid) {
    errors.push(`Invalid unitCount: ${data.unitCount}`);
  }

  // Validate propertyAddress using isOptionalString
  collectError(isOptionalString(data.propertyAddress, 'propertyAddress'), errors);

  // Validate categories
  if (data.categories && typeof data.categories === 'object') {
    const catErrors = validateCategories(
      data.categories as Record<string, unknown>,
      propertyType ?? sowConfig.defaultPropertyType
    );
    errors.push(...catErrors);
  }

  // Validate contingency
  if (data.contingency && typeof data.contingency === 'object') {
    const contErrors = validateContingency(data.contingency as Record<string, unknown>);
    errors.push(...contErrors);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

// Validate categories object
function validateCategories(categories: Record<string, unknown>, propertyType: PropertyType): string[] {
  const errors: string[] = [];

  for (const [categoryId, categoryData] of Object.entries(categories)) {
    // Check category ID is valid
    if (!isValidCategoryId(categoryId, propertyType)) {
      errors.push(`Unknown category ID: ${categoryId}`);
      continue;
    }

    // Use isObject validator for category data check
    const catObjectCheck = isObject(categoryData, `Category ${categoryId}`);
    if (!catObjectCheck.valid) {
      errors.push(`Category ${categoryId}: data must be an object`);
      continue;
    }

    const cat = categoryData as Record<string, unknown>;

    // Validate buildingWide
    if (cat.buildingWide && typeof cat.buildingWide === 'object') {
      const bwErrors = validateFieldValues(categoryId, cat.buildingWide as Record<string, unknown>, propertyType);
      errors.push(...bwErrors.map(e => `Category ${categoryId}.buildingWide: ${e}`));
    }

    // Validate units array
    if (cat.units && Array.isArray(cat.units)) {
      cat.units.forEach((unit, i) => {
        if (unit && typeof unit === 'object') {
          const unitErrors = validateFieldValues(categoryId, unit as Record<string, unknown>, propertyType);
          errors.push(...unitErrors.map(e => `Category ${categoryId}.units[${i}]: ${e}`));
        }
      });
    }

    // Validate description using isOptionalString
    collectError(isOptionalString(cat.description, `Category ${categoryId}: description`), errors);
  }

  return errors;
}

// Validate field values object
function validateFieldValues(categoryId: string, values: Record<string, unknown>, propertyType: PropertyType): string[] {
  const errors: string[] = [];

  for (const [fieldId, value] of Object.entries(values)) {
    // Check field ID is valid for this category
    if (!isValidFieldId(categoryId, fieldId, propertyType)) {
      errors.push(`Unknown field ID: ${fieldId}`);
      continue;
    }

    // Check value is a valid non-negative number using isNonNegativeNumber validator
    const valueResult = isNonNegativeNumber(value, fieldId);
    if (!valueResult.valid) {
      errors.push(`Invalid value for ${fieldId}: ${value}`);
    }
  }

  return errors;
}

// Validate contingency object
function validateContingency(contingency: Record<string, unknown>): string[] {
  const errors: string[] = [];

  // Validate contingency.value using isNonNegativeNumber
  const valueResult = isNonNegativeNumber(contingency.value, 'contingency value');
  if (!valueResult.valid) {
    errors.push(`Invalid contingency value: ${contingency.value}`);
  }

  // Validate contingency.mode using isOneOf
  const modeValidator = isOneOf(['auto', 'manual'] as const);
  const modeResult = modeValidator(contingency.mode as ContingencyMode, 'contingency mode');
  if (!modeResult.valid) {
    errors.push(`Invalid contingency mode: ${contingency.mode}`);
  }

  return errors;
}

// ============================================
// NORMALIZATION
// ============================================

// Normalize partial SOW data to complete SOWData
// Fills in missing fields with defaults, ensures correct structure
export function normalizeSOWData(partial: Partial<SOWData>): SOWData {
  const propertyType = partial.propertyType ?? sowConfig.defaultPropertyType;
  const unitCount = propertyType === PropertyType.SFR
    ? 1
    : Math.max(sowConfig.minUnits, Math.min(partial.unitCount ?? sowConfig.minUnits, sowConfig.maxUnits));

  // Start with initialized empty data
  const base = initializeSOWData(propertyType, unitCount, partial.propertyAddress ?? '');

  // Merge categories
  const categories: Record<string, CategoryData> = {};
  for (const categoryId of Object.keys(base.categories)) {
    categories[categoryId] = normalizeCategoryData(
      categoryId,
      partial.categories?.[categoryId],
      base.categories[categoryId],
      unitCount
    );
  }

  // Build the normalized data
  const normalized: SOWData = {
    propertyAddress: partial.propertyAddress ?? '',
    propertyType,
    unitCount,
    categories,
    contingency: normalizeContingency(partial.contingency, 0), // Will be recalculated
    subtotal: 0, // Will be recalculated
    total: 0, // Will be recalculated
  };

  // Recalculate totals
  return recalculateTotals(normalized);
}

// Normalize a single category's data
function normalizeCategoryData(
  _categoryId: string,
  partial: Partial<CategoryData> | undefined,
  base: CategoryData,
  unitCount: number
): CategoryData {
  // Normalize buildingWide values
  const buildingWide: Record<string, CurrencyValue> = {};
  for (const fieldId of Object.keys(base.buildingWide)) {
    buildingWide[fieldId] = normalizeValue(partial?.buildingWide?.[fieldId]);
  }

  // Normalize unit values
  const units: FieldValues[] = [];
  for (let i = 0; i < unitCount; i++) {
    const unitValues: Record<string, CurrencyValue> = {};
    for (const fieldId of Object.keys(base.buildingWide)) {
      unitValues[fieldId] = normalizeValue(partial?.units?.[i]?.[fieldId]);
    }
    units.push(unitValues);
  }

  return {
    buildingWide,
    units,
    description: partial?.description ?? '',
  };
}

// Normalize a currency value
function normalizeValue(value: unknown): CurrencyValue {
  if (typeof value === 'number' && !isNaN(value) && value >= 0) {
    return Math.round(value);
  }
  return 0;
}

// Normalize contingency data
function normalizeContingency(
  partial: Partial<SOWData['contingency']> | undefined,
  subtotal: CurrencyValue
): SOWData['contingency'] {
  const calculatedValue = calculateContingency(subtotal);
  const mode: ContingencyMode = partial?.mode === 'manual' ? 'manual' : 'auto';
  const rawManualValue = partial?.value;
  const hasManualValue = typeof rawManualValue === 'number'
    && Number.isFinite(rawManualValue)
    && rawManualValue >= 0;
  const normalizedManualValue = hasManualValue
    ? Math.round(rawManualValue)
    : calculatedValue;
  const clampedManualValue = Math.max(sowConfig.contingencyMinimum, normalizedManualValue);

  return {
    value: mode === 'auto' ? calculatedValue : clampedManualValue,
    mode,
    calculatedValue,
  };
}
